import React, { useEffect, useMemo, useRef } from 'react';
import { Alert } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setDialog } from 'src/features/dialog/dialogSlice';
import {
  useGetTenantInformationQuery,
  useSendTenantTestMailMutation,
  useUpdateTenantMailSettingsMutation,
} from 'src/features/tenants/tenantsApiSlice';
import { selectCurrentUserData } from 'src/features/auth/authSlice';
import MailTemplatePageLayout from './MailTemplatePageLayout';

const tokens = [
  {
    value: '{{formalGreeting}}',
    label: 'Formale Anrede',
    desc: 'Automatische Anrede basierend auf dem Titel: Herr → Sehr geehrter Herr [Nachname], Frau → Sehr geehrte Frau [Nachname], kein Titel → Guten Tag [Vollständiger Name].',
  },
  {
    value: '{{memberFirstName}}',
    label: 'Vorname',
    desc: 'Vorname des Mitglieds.',
  },
  {
    value: '{{memberLastName}}',
    label: 'Nachname',
    desc: 'Nachname des Mitglieds.',
  },
  {
    value: '{{memberFullName}}',
    label: 'Vollständiger Name',
    desc: 'Vor- und Nachname des Mitglieds.',
  },
  {
    value: '{{contractOwner}}',
    label: 'Vertragsinhaber',
    desc: 'Name der Person, die den Vertrag abgeschlossen hat.',
  },
  {
    value: '{{contractEmail}}',
    label: 'E-Mail-Adresse',
    desc: 'Die im Vertrag hinterlegte E-Mail-Adresse.',
  },
  {
    value: '{{activationUrl}}',
    label: 'Aktivierungslink',
    desc: 'Link zur Kontoaktivierung für das Mitglieder-Portal – wird automatisch generiert und ist 24 Stunden gültig. Nur enthalten, wenn ein neues Konto erstellt wurde.',
  },
  {
    value: '{{tenantName}}',
    label: 'Vereinsname',
    desc: 'Name Ihres Vereins.',
  },
  {
    value: '{{today}}',
    label: 'Heutiges Datum',
    desc: 'Das aktuelle Datum zum Zeitpunkt des E-Mail-Versands.',
  },
];

const additionalInfo = (
  <Alert severity='info'>
    Bei Genehmigung einer Selbstanmeldung wird die Anmeldebestätigung
    automatisch als PDF-Anhang beigefügt. Dieses Verhalten kann nicht
    deaktiviert werden. Der Aktivierungslink{' '}
    <code>{'{{activationUrl}}'}</code> ist nur vorhanden, wenn für die
    E-Mail-Adresse ein neues Konto erstellt wurde.
  </Alert>
);

const MailSelfRegApprovalTemplateSettings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const currentUserData = useSelector(selectCurrentUserData);
  const { data: tenantInfo, isLoading } = useGetTenantInformationQuery();
  const [updateTenantMailSettings, { isLoading: isSaving }] =
    useUpdateTenantMailSettingsMutation();
  const [sendTenantTestMail, { isLoading: isTesting }] =
    useSendTenantTestMailMutation();

  const defaultValues = useMemo(
    () => ({
      selfRegApprovalSubject:
        tenantInfo?.settings?.mail?.selfRegApprovalSubject ||
        'Anmeldung genehmigt - Anmeldebestätigung',
      selfRegApprovalTemplate:
        tenantInfo?.settings?.mail?.selfRegApprovalTemplate ||
        '{{formalGreeting}},\n\nwir freuen uns, Ihnen mitteilen zu können, dass Ihre Anmeldung bei {{tenantName}} genehmigt wurde!\n\nIm Anhang finden Sie Ihre Anmeldebestätigung als PDF-Dokument. Bitte bewahren Sie dieses Dokument für Ihre Unterlagen auf.\n\n{{#activationUrl}}Mit dem folgenden Link können Sie Ihr Konto erstellen: {{activationUrl}}\n\n{{/activationUrl}}Wir freuen uns, {{memberFullName}} als Mitglied bei {{tenantName}} begrüßen zu dürfen.\n\nNachfolgend finden Sie nochmals eine Zusammenfassung Ihrer Daten:\n\n',
      selfRegApprovalTemplateMode:
        tenantInfo?.settings?.mail?.selfRegApprovalTemplateMode || 'auto',
      selfRegApprovalBcc: tenantInfo?.settings?.mail?.selfRegApprovalBcc || '',
      testRecipient: currentUserData?.mail || '',
    }),
    [tenantInfo, currentUserData]
  );

  const { register, handleSubmit, control, reset, setValue, getValues } =
    useForm({ defaultValues });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const handleInsertToken = (token) => {
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand('insertText', false, token);
      const next = editorRef.current.innerHTML || '';
      setValue('selfRegApprovalTemplate', next, { shouldDirty: true });
      return;
    }
    const current = getValues('selfRegApprovalTemplate') || '';
    setValue(
      'selfRegApprovalTemplate',
      current ? `${current} ${token}` : token,
      { shouldDirty: true }
    );
  };

  const onSubmit = async (data) => {
    const { testRecipient, ...mailSettings } = data;
    try {
      const result = await updateTenantMailSettings(mailSettings).unwrap();
      if (result?.type === 'success') {
        dispatch(
          setDialog({
            title: 'Einstellungen gespeichert',
            type: 'SUCCESS',
            data: { successText: result.message },
          })
        );
      }
    } catch (error) {
      dispatch(
        setDialog({
          title: 'Fehler',
          type: 'ERROR',
          data: {
            errorText:
              error?.data?.message ||
              'Einstellungen konnten nicht gespeichert werden.',
          },
        })
      );
    }
  };

  const handleSendTest = async () => {
    const recipient = (getValues('testRecipient') || '').trim();
    if (!recipient) {
      dispatch(
        setDialog({
          title: 'Hinweis',
          type: 'ERROR',
          data: { errorText: 'Bitte eine Test-E-Mail-Adresse angeben.' },
        })
      );
      return;
    }
    try {
      const result = await sendTenantTestMail({ recipient }).unwrap();
      if (result?.type === 'success') {
        dispatch(
          setDialog({
            title: 'Test-E-Mail gesendet',
            type: 'SUCCESS',
            data: { successText: result.message },
          })
        );
      }
    } catch (error) {
      dispatch(
        setDialog({
          title: 'Fehler',
          type: 'ERROR',
          data: {
            errorText:
              error?.data?.message || 'Test-E-Mail konnte nicht gesendet werden.',
          },
        })
      );
    }
  };

  if (isLoading) return null;

  return (
    <MailTemplatePageLayout
      title='Selbstanmeldung — Genehmigung'
      description='Individualisieren Sie den Text der Genehmigungs-E-Mail, die nach Freigabe einer Selbstanmeldung an das Mitglied versendet wird.'
      subjectFieldName='selfRegApprovalSubject'
      templateFieldName='selfRegApprovalTemplate'
      bccFieldName='selfRegApprovalBcc'
      templateType='selfRegApproval'
      modeFieldName='selfRegApprovalTemplateMode'
      currentHtmlTemplate={tenantInfo?.settings?.mail?.selfRegApprovalHtmlTemplate}
      tokens={tokens}
      additionalInfo={additionalInfo}
      control={control}
      register={register}
      handleInsertToken={handleInsertToken}
      editorRef={editorRef}
      handleSubmit={handleSubmit}
      onSubmit={onSubmit}
      handleSendTest={handleSendTest}
      isSaving={isSaving}
      isTesting={isTesting}
      tenantName={tenantInfo?.tenantName}
      navigate={navigate}
    />
  );
};

export default MailSelfRegApprovalTemplateSettings;
